import { PreclassCheckResultType } from "../shared/types/preclass-check";

import { CoreUserRoleType, PageCategory, UserType } from "components/server-types";
import { NestedOutcomeGroupType } from "store/selectors";

// TODO: This is a type file for all server-related data types
// that are outside the storeAPI.
// A future PR will move `server-types.ts` into this folder too.

// Engagement data

export enum EngagementLevel {
  NONE = "none",
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export type UserEngagementDataType = {
  effort_level: EngagementLevel;
  effort_percentile?: number;
  performance_level: EngagementLevel;
  performance_average?: number;
  risk_level: EngagementLevel;
  attendance_count?: number;
  avg_talk_time_minutes?: number;
};

export type ExtendedUserEngagementDataType = UserType & UserEngagementDataType;

export type AssessmentStatusInfoType = {
  status: AssessmentStatus;
  unassessed_late_count: number;
  assessed_submissions_count: number;
};

export enum AssessmentStatus {
  NOT_ASSESSABLE = "not_assessable",
  ASSESSABLE = "assessable",
  ASSESSED = "assessed",
}

export enum InviteStatus {
  ACCEPTED = "accepted",
  NOT_SENT = "not_sent",
  PENDING = "pending",
  EXPIRED = "expired",
}

export type PageAssessmentStatusType = Record<string, AssessmentStatusInfoType>;
export type UserInviteStatusType = Record<string, InviteStatus>;

export type CourseAssessmentSummaryParams = {
  course_code_id?: string;
  term_id: string;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
};

export type CourseEnrollmentData = {
  term: string;
  course_code: string;
  course_title: string;
  role: CoreUserRoleType;
  name: string;
  email: string;
  disabled_status: "org" | "course" | "both" | "neither";
  external_user_id: string | null;
  external_course_id: string | null;
  invite_status: InviteStatus;
};

export type CourseAssessmentSummaryData = {
  id: string;
  title: string;
  course_code_title: string;
  instructor_names: string[];
  date_last_assessed: string | null;
  assessment_percentage: number;
  comment_percentage: number;
  avg_comment_length: number;
  avg_assessment_delay_days: number;
  avg_student_assessments: number;
};

export type PageAssessmentSummaryData = {
  term: string;
  course: string;
  instructors: string[];
  page_type: PageCategory;
  page_title: string;
  assessments_published_on: string | null;
  assessment_percentage: number;
  students_assessed: number;
  total_students_to_assess: number;
  latest_responses_collected: string | null;
  first_assessment: string | null;
  most_recent_assessment: string | null;
  comment_percentage: number;
  avg_comment_length: number;
  outcomes_assessed: Array<{
    outcome_title: string;
    outcome_id: number;
    avg_score: number;
  }>;
};

export type ProgressOverviewData = {
  averageOutcomeScore: number;
  outcomeGroups: NestedOutcomeGroupType[];
  totalOutcomesAssessed: number;
};

export type FullAssessmentData = {
  student_name: string;
  external_id: string;
  student_email: string;
  term: string;
  course_code: string;
  course_title: string;
  page_type: string;
  page_title: string;
  total_possible_points: number;
  given_points: number | null;
  group_work: boolean;
  grader_name: string;
  grader_email: string;
  date_assessment_last_updated: string;
  comment: string | null;
  outcomes: Array<{
    outcome_id: number;
    outcome_name: string;
    outcome_score: number;
  }>;
};

export interface ImportExternalCourseResponseType {
  title: string;
  description: string;
  course_code: {
    title: string;
  };
  term: {
    title: string;
  };
}

export type ClassSessionViewType = "virtual-class" | "in-person-class";

export interface ClassSessionSummaryType {
  course_id: string;
  org_id: string;
  class_session_id: string;
  start_time: string;
  end_time: string;
  view: ClassSessionViewType;
  students: string[];
  attending_students: string[];
  instructor_talk_time_seconds: number;
  student_talk_time_seconds: number;
  student_presence_minutes?: number | null;
}

export interface AssessmentDataForPageType {
  id: string;
  title: string;
  topic_title: string | null;
  category: string;
  activity_category: string | null;
  outcomes_count: number;
  max_points: number;
}

export type InstructorAssessmentDataForPageType = AssessmentDataForPageType & {
  assessing_marked_complete_at: string | null;
  last_submitted_at: string | null;
  assessments_published_at: string | null;
  assessable_submissions_count: number;
  assessed_submissions_count: number;
  avg_points: number | null;
};

export type StudentAssessmentDataForPageType = AssessmentDataForPageType & {
  submitted_at: string;
  points: number | null;
  comment: string | null;
};

export interface AssessmentDataForCourseType {
  id: string;
  title: string;
  code_title: string;
  term_id: string;
  pages: AssessmentDataForPageType[];
}

export type InstructorAssessmentDataForCourseType = AssessmentDataForCourseType & {
  pages: InstructorAssessmentDataForPageType[];
};
export type StudentAssessmentDataForCourseType = AssessmentDataForCourseType & {
  pages: StudentAssessmentDataForPageType[];
};

export type CourseOutcomeSummaryData = {
  student_id: string;
  student_name: string;
  external_id: string;
  email: string;
  course_id: string;
  term_title: string;
  course_code_title: string;
  course_title: string;
  course_external_id: string | null;
  outcomes: Array<{
    outcome_id: string;
    outcome_title: string;
    avg_score: number;
  }>;
};

export type StudentOutcomeCompetencyData = {
  student_id: string;
  student_name: string;
  external_id: string;
  email: string;
  outcomes: Array<{
    outcome_id: string;
    outcome_title: string;
    avg_score: number;
  }>;
  outcome_groups: Array<{
    group_id: string;
    group_title: string;
    avg_score: number;
  }>;
  outcome_subgroups: Array<{
    subgroup_id: string;
    subgroup_title: string;
    avg_score: number;
  }>;
};

export type CourseGradesData = {
  student_id: string;
  student_name: string;
  external_id: string;
  email: string;
  course_id: string;
  term_title: string;
  course_code_title: string;
  course_title: string;
  course_external_id: string | null;
  course_score: number;
  prep_score: number;
  activities_score: number;
  assignments_score: number;
  assessed_pages_count: number;
};

export type SystemCheckResultsData = {
  user_full_name: string;
  user_email: string;
  user_created_at: string;
  ip: string;
  check_end_result: "passed" | "failed" | "na";
  test_results: PreclassCheckResultType;
  created_at: string;
  browser: string | null;
  os: string | null;
};
